import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  // Check if we're on a legal page
  const isLegalPage = location.pathname !== '/';

  // Optimized scroll handler with throttling
  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    setIsScrolled(scrollY > 20);
  }, []);

  useEffect(() => {
    let ticking = false;
    
    const optimizedScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', optimizedScrollHandler, { passive: true });
    return () => window.removeEventListener('scroll', optimizedScrollHandler);
  }, [handleScroll]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('nav')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen]);

  const scrollToSection = useCallback((sectionId) => {
    if (isLegalPage) {
      // On legal pages, navigate to main website
      window.location.href = `/#${sectionId}`;
    } else {
      // On main website, scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else {
        // Section not found - silently handle
      }
    }
    setIsMobileMenuOpen(false);
  }, [isLegalPage]);

  // Handle hash navigation when page loads
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash;
      if (hash) {
        const sectionId = hash.replace('#', '');
        const element = document.getElementById(sectionId);
        if (element) {
          // Wait a bit for the page to fully load
          setTimeout(() => {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }, 100);
        }
      }
    };

    // Handle initial hash
    handleHashNavigation();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashNavigation);
    
    return () => window.removeEventListener('hashchange', handleHashNavigation);
  }, []);

  const navItems = useMemo(() => [
    { name: 'Features', id: 'features' },
    { name: 'How It Works', id: 'how-it-works' },
    { name: 'About', id: 'about' },
    { name: 'Contact', id: 'contact' },
  ], []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 gpu-accelerated ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200'
          : isLegalPage 
            ? 'bg-white/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
      }`}
      style={{ willChange: isScrolled ? 'auto' : 'background-color, box-shadow' }}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 lg:h-20 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={() => {
                if (isLegalPage) {
                  window.location.href = '/';
                } else {
                  scrollToSection('hero');
                }
              }}
              className="transition-all duration-200 hover:scale-105 focus:scale-105 focus-ring gpu-accelerated"
              aria-label="CIVIONIKS - Go to homepage"
            >
              <img 
                src="/logo with words.png" 
                alt="CIVIONIKS" 
                className="h-32 lg:h-40 w-auto"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-105 focus-ring gpu-accelerated ${
                    isScrolled
                      ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                      : isLegalPage
                        ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.name}
                  <span className={`absolute bottom-0 left-1/2 w-0 h-0.5 bg-primary-500 transition-all duration-300 transform -translate-x-1/2 group-hover:w-full`}></span>
                </button>
              ))}
              
              <div className="ml-4 pl-4 border-l border-white/20">
                <button
                  onClick={() => {
                    if (isLegalPage) {
                      window.location.href = '/#api-access';
                    } else {
                      scrollToSection('api-access');
                    }
                  }}
                  className={`btn-primary text-sm px-6 py-2.5 gpu-accelerated ${
                    !isScrolled ? 'shadow-lg hover:shadow-xl' : ''
                  }`}
                >
                  <span>API Access</span>
                  <svg className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`relative p-2 rounded-lg transition-all duration-200 focus-ring gpu-accelerated ${
                isScrolled
                  ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  : isLegalPage
                    ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            >
              <div className="w-6 h-6 relative flex flex-col justify-center items-center">
                <span className={`block w-6 h-0.5 transition-all duration-300 absolute ${
                  isLegalPage ? 'bg-gray-700' : 'bg-white'
                } ${isMobileMenuOpen ? 'rotate-45' : '-translate-y-1.5'}`}></span>
                <span className={`block w-6 h-0.5 transition-all duration-300 absolute ${
                  isLegalPage ? 'bg-gray-700' : 'bg-white'
                } ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
                <span className={`block w-6 h-0.5 transition-all duration-300 absolute ${
                  isLegalPage ? 'bg-gray-700' : 'bg-white'
                } ${isMobileMenuOpen ? '-rotate-45' : 'translate-y-1.5'}`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden transition-all duration-300 overflow-hidden gpu-accelerated z-50 ${isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="px-4 pb-4 relative z-50" id="mobile-menu">
            <div className={`rounded-2xl shadow-large border p-4 mt-2 relative z-50 ${
              isLegalPage 
                ? 'bg-white border-gray-200' 
                : 'bg-white/10 backdrop-blur-optimized border-white/20'
            }`}>
              <nav className="space-y-2" role="navigation" aria-label="Mobile navigation">
                {navItems.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`flex items-center w-full px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 focus-ring gpu-accelerated ${
                      isLegalPage
                        ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                    }`}
                    aria-label={`Navigate to ${item.name} section`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <span>{item.name}</span>
                    <svg className="w-4 h-4 ml-auto opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
                
                <div className="pt-4 mt-4 border-t border-white/20">
                  <button
                    onClick={() => {
                      if (isLegalPage) {
                        window.location.href = '/#api-access';
                      } else {
                        scrollToSection('api-access');
                      }
                    }}
                    className={`w-full justify-center group gpu-accelerated ${
                      isLegalPage
                        ? 'btn-primary'
                        : 'btn-primary'
                    }`}
                    aria-label="Navigate to API Access form"
                  >
                    <span>Get API Access</span>
                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                </div>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-gray-900/20 backdrop-blur-optimized md:hidden z-[-1] gpu-accelerated"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        ></div>
      )}
    </nav>
  );
};

export default Navigation; 